import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSavedListings } from '../context/SavedListingsContext';
import { useAuth } from '../context/AuthContext';
import { inboxApi } from '../api/client';
import { SUPPORTED_LANGS } from '../i18n';

export default function Layout() {
  const { t, i18n } = useTranslation();
  const [locationQuery, setLocationQuery] = useState('');
  const navigate = useNavigate();
  const { count: savedCount } = useSavedListings();
  const { state: authState, profile, signOut, sessionInvalid } = useAuth();
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);

  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Only fetch unread count once we have a validated session (profile loaded). Avoids 401 storm when token isn't ready yet.
  useEffect(() => {
    if (authState.status !== 'authenticated' || profile == null) {
      setInboxUnreadCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = () => {
      inboxApi
        .getUnreadCount()
        .then((r) => {
          if (!cancelled && typeof r.unreadCount === 'number') setInboxUnreadCount(r.unreadCount);
        })
        .catch(() => {
          if (cancelled) return;
          // Don't retry on 401; next focus or poll will try again
        });
    };
    fetchCount();
    const onInboxUpdated = () => fetchCount();
    const onFocus = () => fetchCount();
    window.addEventListener('inbox-updated', onInboxUpdated);
    window.addEventListener('focus', onFocus);
    const pollInterval = setInterval(fetchCount, 45000);
    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      window.removeEventListener('inbox-updated', onInboxUpdated);
      window.removeEventListener('focus', onFocus);
    };
  }, [authState.status, profile]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = locationQuery.trim();
    const params = new URLSearchParams();
    if (q) params.set('location', q);
    navigate(params.toString() ? `/listings?${params.toString()}` : '/listings');
  };

  return (
    <>
      {/* Top accent bar - fixed width so identical in LTR/RTL */}
      <div
        style={{
          height: 4,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          background: 'linear-gradient(90deg, #0d9488 0%, #14b8a6 100%)',
        }}
      />
      {sessionInvalid && (
        <div
          style={{
            padding: '0.75rem 1.25rem',
            background: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            color: '#b91c1c',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <span>
            {t('dashboard.sessionInvalid')}
          </span>
          <button
            type="button"
            onClick={() => signOut()}
            style={{
              padding: '0.35rem 0.75rem',
              background: '#b91c1c',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('common.signOut')}
          </button>
        </div>
      )}
      <header className="app-header">
        <div className="app-header-inner">
        <Link to="/" className="app-header-logo" aria-label="Darlynk Home">
          <span className="app-header-logo-icon" />
          <span className="app-header-logo-text">Darlynk</span>
        </Link>
        <form onSubmit={handleSearch} className="app-header-search">
          <input
            type="text"
            placeholder={t('common.searchPlaceholder')}
            value={locationQuery}
            onChange={(e) => setLocationQuery(e.target.value)}
            aria-label={t('common.search')}
          />
          <button type="submit" aria-label={t('common.search')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </button>
        </form>
        <nav className="app-header-nav">
          <Link to="/" className="app-header-link">{t('common.home')}</Link>
          <Link to="/listings?type=sale" className="app-header-link">{t('common.buy')}</Link>
          <Link to="/listings?type=rent" className="app-header-link">{t('common.rent')}</Link>
          <Link to="/listings?type=short_term" className="app-header-link app-header-link--short">{t('common.shortTerm')}</Link>
          <Link to="/dashboard" className="app-header-link">{t('common.dashboard')}</Link>
          <Link to="/listings/compare" className="app-header-link app-header-link--compare">{t('compare.title')}</Link>
          <Link to="/saved" className="app-header-icon" aria-label={t('common.saved')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            {savedCount > 0 && <span className="app-header-badge">{savedCount > 99 ? '99+' : savedCount}</span>}
          </Link>
          <a href="/guide" target="_blank" rel="noopener noreferrer" className="app-header-icon" title={i18n.language === 'ar' ? 'دليل المستخدم' : 'User guide'} aria-label={i18n.language === 'ar' ? 'دليل المستخدم' : 'User guide'}>?</a>
          {authState.status === 'authenticated' ? (
            <Link to="/dashboard/leads" className="app-header-inbox" aria-label={t('dashboard.leadsInbox')} title={t('dashboard.leadsInbox')}>
              <span className="app-header-inbox-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {inboxUnreadCount > 0 && <span className="app-header-badge app-header-badge--dot">{inboxUnreadCount > 99 ? '99+' : inboxUnreadCount}</span>}
              </span>
              <span className="app-header-inbox-label">{t('dashboard.inboxTitle')}</span>
            </Link>
          ) : (
            <span className="app-header-icon app-header-icon--muted" aria-label={t('dashboard.leadsInbox')}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </span>
          )}
          {authState.status === 'authenticated' ? (
            <span className="app-header-user">
              <span className="app-header-user-email" title={authState.user.email}>{authState.user.email}</span>
              <button type="button" onClick={() => signOut()} className="app-header-btn">{t('common.signOut')}</button>
            </span>
          ) : (
            <Link to="/login" className="app-header-btn">{t('common.signIn')}</Link>
          )}
          <span className="app-header-lang">
            {SUPPORTED_LANGS.map((lng) => (
              <button
                key={lng}
                type="button"
                onClick={() => i18n.changeLanguage(lng)}
                className={i18n.language === lng ? 'app-header-lang-btn is-active' : 'app-header-lang-btn'}
                aria-label={lng === 'ar' ? 'العربية' : 'English'}
              >
                {lng === 'ar' ? 'عربي' : 'English'}
              </button>
            ))}
          </span>
        </nav>
        </div>
      </header>

      <main
        style={{
          minHeight: 'calc(100vh - 140px)',
          background: '#f9fafb',
          padding: '1.5rem',
        }}
      >
        <Outlet />
      </main>

      <footer
        style={{
          padding: '1.25rem 1.5rem',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '0.875rem',
          borderTop: '1px solid #e5e7eb',
          background: '#fff',
        }}
      >
        {t('common.footer')}
      </footer>
    </>
  );
}
